import React, { useState, useEffect, useCallback } from 'react';
import { batchService } from '../services';
import { formatBatchDates } from '../utils/studentDisplay';
import {
  getBatchStudentCount,
  getBatchCapacity,
  isBatchAtCapacity,
  getBatchSeatsLeft,
} from '../utils/batchCapacity';

const TRACK_OPTIONS = [
  { value: 'secondary', label: 'Secondary', icon: '🎓' },
  { value: 'professional', label: 'Professional', icon: '💼' },
];

const normalizeBatchStatus = (status) => {
  if (status === 'planned') return 'closed';
  return status === 'running' ? 'running' : 'closed';
};

const emptyForm = () => ({
  name: '',
  track: 'secondary',
  startDate: '',
  expectedEndDate: '',
  maxCapacity: '',
});

const BatchManagement = () => {
  const [batches, setBatches] = useState([]);
  const [statsMap, setStatsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [trackFilter, setTrackFilter] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchBatches = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = trackFilter ? { track: trackFilter } : {};
      const data = await batchService.getAllBatches(params);
      const list = Array.isArray(data) ? data : [];
      setBatches(list);

      const statsEntries = await Promise.all(
        list.map(async (b) => {
          try {
            const stats = await batchService.getBatchStats(b._id);
            return [b._id, stats];
          } catch {
            return [b._id, { total: 0, byProgram: [] }];
          }
        })
      );
      setStatsMap(Object.fromEntries(statsEntries));
    } catch (err) {
      setError(err.error || err.message || 'Failed to load batches');
    } finally {
      setLoading(false);
    }
  }, [trackFilter]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  useEffect(() => {
    if (successMessage) {
      const t = setTimeout(() => setSuccessMessage(''), 4000);
      return () => clearTimeout(t);
    }
  }, [successMessage]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const buildPayload = () => {
    const payload = {
      name: formData.name.trim(),
      track: formData.track,
      status: selectedBatch
        ? normalizeBatchStatus(selectedBatch.status)
        : 'running',
      startDate: formData.startDate,
      expectedEndDate: formData.expectedEndDate,
    };
    if (formData.maxCapacity !== '' && formData.maxCapacity != null) {
      payload.maxCapacity = Number(formData.maxCapacity);
    }
    return payload;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await batchService.createBatch(buildPayload());
      setSuccessMessage('Batch created.');
      resetForm();
      fetchBatches();
    } catch (err) {
      setError(err.error || err.message || 'Failed to create batch');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedBatch) return;
    setError('');
    try {
      await batchService.updateBatch(selectedBatch._id, buildPayload());
      setSuccessMessage('Batch updated.');
      resetForm();
      fetchBatches();
    } catch (err) {
      setError(err.error || err.message || 'Failed to update batch');
    }
  };

  const handleStatusChange = async (batch, status) => {
    setError('');
    try {
      await batchService.updateBatchStatus(batch._id, status);
      setSuccessMessage(
        status === 'running'
          ? `"${batch.name}" is open for ${batch.track} registration.`
          : `"${batch.name}" is closed.`
      );
      fetchBatches();
    } catch (err) {
      setError(err.error || err.message || 'Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    setError('');
    try {
      const result = await batchService.deleteBatch(id);
      const unassigned = result?.unassignedStudents || 0;
      setSuccessMessage(
        unassigned > 0
          ? `Batch deleted. ${unassigned} student${unassigned === 1 ? '' : 's'} unassigned (no longer in a cohort).`
          : (result?.message || 'Batch deleted.')
      );
      setDeleteConfirm(null);
      fetchBatches();
    } catch (err) {
      setError(err.error || err.message || 'Failed to delete batch');
      setDeleteConfirm(null);
    }
  };

  const handleEdit = (batch) => {
    setSelectedBatch(batch);
    setFormData({
      name: batch.name || '',
      track: batch.track || 'secondary',
      startDate: batch.startDate?.split?.('T')[0] || '',
      expectedEndDate: batch.expectedEndDate?.split?.('T')[0] || '',
      maxCapacity: batch.maxCapacity ?? '',
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData(emptyForm());
    setSelectedBatch(null);
    setShowForm(false);
  };

  const statusBadgeClass = (status) => {
    const s = normalizeBatchStatus(status);
    if (s === 'running') return 'bm-badge bm-badge-running';
    return 'bm-badge bm-badge-closed';
  };

  const statusLabel = (status) => {
    const s = normalizeBatchStatus(status);
    return s === 'running' ? 'open' : 'closed';
  };

  return (
    <>
      <style>{`
        .bm-root {
          padding: 24px;
          min-height: 100vh;
          background: var(--bg);
          font-family: system-ui, -apple-system, sans-serif;
          color: var(--text);
        }
        .bm-header {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 24px;
          box-shadow: 0 1px 3px rgb(0 0 0 / 0.08);
        }
        .bm-eyebrow {
          display: inline-block;
          background: #1e40af;
          color: #fff;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 4px 12px;
          border-radius: 12px;
          margin-bottom: 10px;
        }
        .bm-title { font-size: 26px; font-weight: 700; margin-bottom: 6px; }
        .bm-sub { color: var(--muted); font-size: 15px; max-width: 560px; }
        .bm-toolbar {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin: 20px 0;
          align-items: center;
        }
        .bm-select, .bm-input {
          padding: 10px 14px;
          border: 1px solid var(--border-md);
          border-radius: 8px;
          font-size: 14px;
          font-family: inherit;
        }
        .bm-btn {
          padding: 10px 18px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          border: none;
          font-family: inherit;
        }
        .bm-btn-primary { background: #1e40af; color: #fff; }
        .bm-btn-primary:hover { background: #1e3a8a; }
        .bm-btn-ghost {
          background: var(--surface);
          color: var(--text2);
          border: 1px solid var(--border-md);
        }
        .bm-btn-sm {
          padding: 6px 12px;
          font-size: 12px;
          border-radius: 6px;
          border: 1px solid var(--border-md);
          background: var(--surface);
          cursor: pointer;
          font-weight: 500;
        }
        .bm-btn-sm:hover { border-color: #1e40af; color: #1e40af; }
        .bm-btn-danger { color: #dc2626; border-color: #fecaca; }
        .bm-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 20px;
        }
        .bm-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 1px 3px rgb(0 0 0 / 0.06);
        }
        .bm-card-head {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 12px;
        }
        .bm-card-name { font-size: 17px; font-weight: 700; }
        .bm-card-track { font-size: 12px; color: var(--muted); margin-top: 4px; }
        .bm-badge {
          display: inline-flex;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          text-transform: capitalize;
        }
        .bm-badge-running { background: #d1fae5; color: #059669; }
        .bm-badge-closed { background: var(--bg); color: var(--muted); }
        .bm-hint { font-size: 12px; color: var(--muted); margin-top: 4px; }
        .bm-stat-row {
          display: flex;
          gap: 16px;
          margin: 14px 0;
          padding: 12px 0;
          border-top: 1px solid #f3f4f6;
          border-bottom: 1px solid #f3f4f6;
        }
        .bm-stat { flex: 1; text-align: center; }
        .bm-stat-num { font-size: 22px; font-weight: 700; color: #1e40af; }
        .bm-stat-num.full { color: #dc2626; }
        .bm-stat-label { font-size: 11px; color: var(--muted); text-transform: uppercase; }
        .bm-capacity-bar {
          height: 8px;
          border-radius: 999px;
          background: #e5e7eb;
          overflow: hidden;
          margin: 4px 0 6px;
        }
        .bm-capacity-fill {
          height: 100%;
          border-radius: 999px;
          background: linear-gradient(90deg, #10b981, #059669);
          transition: width 0.3s ease;
        }
        .bm-capacity-fill.full {
          background: linear-gradient(90deg, #f59e0b, #dc2626);
        }
        .bm-capacity-meta {
          font-size: 12px;
          color: var(--muted);
          margin-bottom: 10px;
        }
        .bm-capacity-meta.full { color: #b45309; font-weight: 700; }
        .bm-capacity-alert {
          font-size: 12px;
          line-height: 1.4;
          color: #92400e;
          background: #fffbeb;
          border: 1px solid #fde68a;
          border-radius: 8px;
          padding: 8px 10px;
          margin-bottom: 10px;
        }
        .bm-btn-sm:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          border-color: var(--border);
          color: #9ca3af;
        }
        .bm-programs { font-size: 12px; color: #4b5563; line-height: 1.5; }
        .bm-programs span { display: inline-block; margin-right: 8px; margin-bottom: 4px; background: var(--bg); padding: 2px 8px; border-radius: 4px; }
        .bm-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 14px; }
        .bm-form-panel {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 24px;
        }
        .bm-form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        .bm-label { font-size: 13px; font-weight: 600; margin-bottom: 6px; display: block; }
        .bm-toast {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 1000;
          padding: 14px 18px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          box-shadow: 0 4px 12px rgb(0 0 0 / 0.15);
        }
        .bm-toast-ok { background: #ecfdf5; color: #059669; border-left: 4px solid #059669; }
        .bm-toast-err { background: #fef2f2; color: #dc2626; border-left: 4px solid #dc2626; }
        .bm-empty { text-align: center; padding: 48px; color: var(--muted); }
        .bm-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
          padding: 20px;
        }
        .bm-modal {
          background: var(--surface);
          border-radius: 12px;
          padding: 24px;
          max-width: 400px;
          width: 100%;
        }
        @media (max-width: 640px) {
          .bm-form-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="bm-root">
        {successMessage && <div className="bm-toast bm-toast-ok">{successMessage}</div>}
        {error && <div className="bm-toast bm-toast-err">{error}</div>}

        <div className="bm-header">
          <div className="bm-eyebrow">Cohorts</div>
          <h1 className="bm-title">Batch Management</h1>
          <p className="bm-sub">
            One running batch per track (Secondary and Professional). New student registrations are
            assigned automatically to the active cohort for their track.
          </p>
        </div>

        <div className="bm-toolbar">
          <select
            className="bm-select"
            value={trackFilter}
            onChange={(e) => setTrackFilter(e.target.value)}
          >
            <option value="">All tracks</option>
            <option value="secondary">Secondary</option>
            <option value="professional">Professional</option>
          </select>
          <button
            type="button"
            className="bm-btn bm-btn-ghost"
            onClick={fetchBatches}
          >
            Refresh
          </button>
          <button
            type="button"
            className="bm-btn bm-btn-primary"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            + New Batch
          </button>
        </div>

        {showForm && (
          <div className="bm-form-panel">
            <h2 style={{ fontSize: 18, marginBottom: 16 }}>
              {selectedBatch ? 'Edit Batch' : 'Create Batch'}
            </h2>
            <form onSubmit={selectedBatch ? handleUpdate : handleCreate}>
              <div className="bm-form-grid">
                <div>
                  <label className="bm-label">Name</label>
                  <input
                    className="bm-input"
                    style={{ width: '100%' }}
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g. Secondary - 2026 Term 1"
                    required
                  />
                </div>
                <div>
                  <label className="bm-label">Track</label>
                  <select
                    className="bm-select"
                    style={{ width: '100%' }}
                    name="track"
                    value={formData.track}
                    onChange={handleInputChange}
                    disabled={!!selectedBatch}
                  >
                    {TRACK_OPTIONS.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="bm-label">Start date</label>
                  <input
                    className="bm-input"
                    style={{ width: '100%' }}
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div>
                  <label className="bm-label">Expected end date</label>
                  <input
                    className="bm-input"
                    style={{ width: '100%' }}
                    type="date"
                    name="expectedEndDate"
                    value={formData.expectedEndDate}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div>
                  <label className="bm-label">Max capacity (optional)</label>
                  <input
                    className="bm-input"
                    style={{ width: '100%' }}
                    type="number"
                    name="maxCapacity"
                    min="1"
                    value={formData.maxCapacity}
                    onChange={handleInputChange}
                  />
                  <div className="bm-hint">When set, new students cannot join once this limit is reached.</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <button type="submit" className="bm-btn bm-btn-primary">
                  {selectedBatch ? 'Save changes' : 'Create batch'}
                </button>
                <button type="button" className="bm-btn bm-btn-ghost" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="bm-empty">Loading batches…</div>
        ) : batches.length === 0 ? (
          <div className="bm-empty">
            No batches yet. Create a batch to start accepting student registrations for a track.
          </div>
        ) : (
          <div className="bm-grid">
            {batches.map((batch) => {
              const stats = statsMap[batch._id] || { total: 0, byProgram: [] };
              const trackMeta = TRACK_OPTIONS.find((t) => t.value === batch.track);
              const studentCount = getBatchStudentCount(batch, stats);
              const capacity = getBatchCapacity(batch);
              const atCapacity = isBatchAtCapacity(batch, stats);
              const seatsLeft = getBatchSeatsLeft(batch, stats);
              const fillPct = capacity ? Math.min(100, Math.round((studentCount / capacity) * 100)) : 0;
              return (
                <div key={batch._id} className="bm-card">
                  <div className="bm-card-head">
                    <div>
                      <div className="bm-card-name">
                        {trackMeta?.icon} {batch.name}
                      </div>
                      <div className="bm-card-track">
                        {trackMeta?.label} · {formatBatchDates(batch)}
                      </div>
                    </div>
                    <span className={statusBadgeClass(batch.status)}>{statusLabel(batch.status)}</span>
                  </div>
                  <div className="bm-stat-row">
                    <div className="bm-stat">
                      <div className={`bm-stat-num${atCapacity ? ' full' : ''}`}>{studentCount}</div>
                      <div className="bm-stat-label">Students</div>
                    </div>
                    {capacity != null ? (
                      <div className="bm-stat">
                        <div className={`bm-stat-num${atCapacity ? ' full' : ''}`}>{capacity}</div>
                        <div className="bm-stat-label">Capacity</div>
                      </div>
                    ) : null}
                  </div>
                  {capacity != null && (
                    <>
                      <div className="bm-capacity-bar" aria-hidden="true">
                        <div
                          className={`bm-capacity-fill${atCapacity ? ' full' : ''}`}
                          style={{ width: `${fillPct}%` }}
                        />
                      </div>
                      <div className={`bm-capacity-meta${atCapacity ? ' full' : ''}`}>
                        {atCapacity
                          ? `Full — ${studentCount}/${capacity} seats used`
                          : `${seatsLeft} seat${seatsLeft === 1 ? '' : 's'} left (${studentCount}/${capacity})`}
                      </div>
                      {atCapacity && (
                        <div className="bm-capacity-alert">
                          This batch is at capacity. New students cannot join until you increase max capacity or remove students.
                        </div>
                      )}
                    </>
                  )}
                  {stats.byProgram?.length > 0 && (
                    <div className="bm-programs">
                      {stats.byProgram.map((p) => (
                        <span key={p.program}>
                          {p.program}: {p.count}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="bm-actions">
                    {normalizeBatchStatus(batch.status) !== 'running' && (
                      <button
                        type="button"
                        className="bm-btn-sm"
                        disabled={atCapacity}
                        title={atCapacity ? 'Batch is full — increase capacity before opening registration' : undefined}
                        onClick={() => handleStatusChange(batch, 'running')}
                      >
                        Open registration
                      </button>
                    )}
                    {normalizeBatchStatus(batch.status) === 'running' && (
                      <button
                        type="button"
                        className="bm-btn-sm"
                        onClick={() => handleStatusChange(batch, 'closed')}
                      >
                        Close registration
                      </button>
                    )}
                    <button type="button" className="bm-btn-sm" onClick={() => handleEdit(batch)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="bm-btn-sm bm-btn-danger"
                      onClick={() => setDeleteConfirm(batch)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {deleteConfirm && (() => {
          const studentCount = statsMap[deleteConfirm._id]?.total ?? 0;
          return (
            <div className="bm-modal-overlay" onClick={() => setDeleteConfirm(null)}>
              <div className="bm-modal" onClick={(e) => e.stopPropagation()}>
                <h3 style={{ marginBottom: 8 }}>Delete batch?</h3>
                <p style={{ color: '#6b7280', marginBottom: 12 }}>
                  Delete “{deleteConfirm.name}”? This cannot be undone.
                </p>
                {studentCount > 0 && (
                  <p
                    style={{
                      color: '#92400e',
                      background: '#fffbeb',
                      border: '1px solid #fde68a',
                      borderRadius: 8,
                      padding: '10px 12px',
                      fontSize: 13,
                      lineHeight: 1.45,
                      marginBottom: 16,
                    }}
                  >
                    This cohort has <strong>{studentCount}</strong> student
                    {studentCount === 1 ? '' : 's'}. They will stay in the system
                    but be <strong>unassigned</strong> from this batch. Reassign
                    them later from Student Management if needed.
                  </p>
                )}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    type="button"
                    className="bm-btn bm-btn-primary"
                    style={{ background: '#dc2626' }}
                    onClick={() => handleDelete(deleteConfirm._id)}
                  >
                    {studentCount > 0 ? 'Delete & unassign students' : 'Delete'}
                  </button>
                  <button
                    type="button"
                    className="bm-btn bm-btn-ghost"
                    onClick={() => setDeleteConfirm(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </>
  );
};

export default BatchManagement;
